import { Chart } from "@antv/g2";
import * as _ from "@antv/util";
import React, { useEffect, useRef, useState } from "react";
import { Empty, Flex, theme } from "antd";
import { createRoot } from "react-dom/client";

export default function DoughnutChart({
  data = [],
  y = "name",
  color = "value",
  innerRadius = 0.5,
  outerRadius = 0.8,
  yValueFormatter = (d) => d,
  statisticValueFormatter = (d) => d.toLocaleString(),
  labelFontSize = 14,
  statisticTitleFontSize = 28,
  statisticValueFontSize = 24,
  style = {},
}) {
  const container = _.uniqueId("piechart-");
  const chartRef = useRef(null);
  const { token } = theme.useToken();
  const [statisticTitle, setStatisticTitle] = useState("Total");
  const [statisticValue, setStatisticValue] = useState(
    data.length > 0
      ? data?.reduce((accumulator, item) => {
          return accumulator + item[y];
        }, 0)
      : 0
  );

  useEffect(() => {
    if (data.length !== 0) {
      chartRef.current?.clear();
      const chartContainer = document.getElementById(container);
      chartContainer.innerHTML = "";

      const chart = new Chart({
        container: container,
        width: container.offsetWidth,
        height: container.offsetHeight,
        padding: "auto",
        autoFit: true,
      });
      chart.coordinate({
        type: "theta",
        innerRadius: innerRadius,
        outerRadius: outerRadius,
      });

      chart
        .interval()
        .data(data)
        .transform({ type: "stackY" })
        .encode("y", y)
        .encode("color", color)
        .label({
          text: y,
          position: "outside",
          fill: token.colorTextBase,
          fontSize: labelFontSize,
          connectorStroke: token.colorTextBase,
          lineDash: [1, 0.5],
          formatter: yValueFormatter,
        })
        .label({
          formatter: (text, datum, index, data) => {
            const sum = data?.reduce((accumulator, item) => {
              return accumulator + item[y];
            }, 0);

            return ((datum[y] / sum) * 100 ?? 0).toFixed(1) + " %";
          },
          position: "inside",
          fill: token.colorTextBase,
          connectorStroke: token.colorTextBase,
        })
        .legend("color", {
          position: "bottom",
          itemMarkerSize: 10,
          itemLabelFontSize: 10,
          itemLabelFill: token.colorTextBase,
          itemValueFontSize: 1,
        })
        .tooltip(false)
        .state("active", { stroke: "white", strokeWidth: 3 })
        .state("inactive", { opacity: 0.5 });

      chart.interaction("elementHighlight", true);
      chart.interaction("legendFilter", false);

      chart.on(`element:highlight`, (e) => {
        const title = document.getElementById(`${container}-statistic-title`);
        title.innerText = e.data.data[color] ?? "";

        const value = document.getElementById(`${container}-statistic-value`);
        value.innerText = statisticValueFormatter(e.data.data.value);
      });
      chart.on(`element:unhighlight`, (e) => {
        const title = document.getElementById(`${container}-statistic-title`);
        title.innerText = "Total";

        const sum = data?.reduce((accumulator, item) => {
          return accumulator + item[y];
        }, 0);

        const value = document.getElementById(`${container}-statistic-value`);
        value.innerText = statisticValueFormatter(sum) ?? "";
      });

      chart.render().then((chart) => {
        const chartContainer = chart.getContainer();

        const Statistic = () => {
          const sum = data?.reduce((accumulator, item) => {
            return accumulator + item[y];
          }, 0);

          return (
            <Flex vertical align="center">
              <p
                id={`${container}-statistic-title`}
                style={{
                  marginTop: 0,
                  marginBottom: 8,
                  fontSize: statisticTitleFontSize,
                }}
              >
                Total
              </p>
              <p
                id={`${container}-statistic-value`}
                style={{
                  marginTop: 0,
                  marginBottom: 0,
                  textWrap: "nowrap",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: statisticValueFontSize,
                }}
              >
                {statisticValueFormatter(sum)}
              </p>
            </Flex>
          );
        };

        const statisticContainer = document.createElement("div");
        statisticContainer.id = `${container}-statistic-container`;
        statisticContainer.style.cssText = `
      position: absolute;
      top: 44%;
      left: 50%;
      transform: translate(-50%, -48%);
      text-align: center;
    `;
        chartContainer.appendChild(statisticContainer);

        const root = createRoot(statisticContainer);
        root.render(
          <Statistic title={statisticTitle} value={statisticValue} />
        );
      });

      chartRef.current = chart;

      return () => {
        chartRef.current?.destroy();
      };
    }
  }, [token, data]);

  useEffect(() => {
    if (data.length !== 0) {
      const chartContainer = document.getElementById(container);
      const resizeObserver = new ResizeObserver(() => {});

      resizeObserver.observe(chartContainer);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  return (
    <>
      {data.length === 0 ? <Empty /> : <div style={style} id={container}></div>}
    </>
  );
}
